import React, { useState, useEffect, JSX } from 'react';
import {
  Workflow,
  Zap,
  Brain,
  Settings,
  Play,
  Pause,
  GitBranch,
  Clock,
  Users,
  Database,
  Globe,
  Shield,
  BarChart3,
  CheckCircle,
  AlertCircle,
  ArrowRight,
  Sparkles,
  Bot,
  MousePointer,
  Layers,
  Network,
  Target,
  Activity,
  TrendingUp,
  Eye,
  Code,
  Cpu,
  MessageSquare,
  FileText,
  Download,
  Upload,
  Copy,
  Edit,
  Trash2,
  Plus,
  Search,
  Filter,
  MoreHorizontal,
  Triangle,
  Square,
  Circle,
  Diamond,
  Hexagon,
  Timer,
  Webhook,
  UserCheck,
  Route
} from 'lucide-react';

interface WorkflowNode {
  type: string;
  icon: JSX.Element;
  color: string;
  description: string;
}

const SelficWorkflowsBody = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [activeWorkflow, setActiveWorkflow] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [draggedNode, setDraggedNode] = useState<WorkflowNode | null>(null);

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveWorkflow((prev) => (prev + 1) % 3);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const workflowNodes = [
    {
      type: 'Start',
      icon: <Circle className="w-4 h-4" />,
      color: 'bg-green-500',
      description: 'Trigger point for workflows'
    },
    {
      type: 'Condition / If-Else',
      icon: <GitBranch className="w-4 h-4" />,
      color: 'bg-blue-500',
      description: 'Conditional logic and branching'
    },
    {
      type: 'Loop / For Each',
      icon: <Square className="w-4 h-4" />,
      color: 'bg-blue-600',
      description: 'Iterate over data collections'
    },
    {
      type: 'Wait / Delay',
      icon: <Timer className="w-4 h-4" />,
      color: 'bg-purple-500',
      description: 'Add delays and scheduling'
    },
    {
      type: 'Webhook Trigger',
      icon: <Webhook className="w-4 h-4" />,
      color: 'bg-cyan-500',
      description: 'External system integration'
    },
    {
      type: 'Human Approval',
      icon: <UserCheck className="w-4 h-4" />,
      color: 'bg-gray-500',
      description: 'Manual review and approval'
    },
    {
      type: 'LLM: GPT-4',
      icon: <Brain className="w-4 h-4" />,
      color: 'bg-green-600',
      description: 'OpenAI GPT-4 integration'
    },
    {
      type: 'LLM: Claude 3',
      icon: <Brain className="w-4 h-4" />,
      color: 'bg-orange-500',
      description: 'Anthropic Claude integration'
    },
    {
      type: 'LLM: Gemini Pro',
      icon: <Brain className="w-4 h-4" />,
      color: 'bg-blue-400',
      description: 'Google Gemini integration'
    },
    {
      type: 'LLM: Mixtral',
      icon: <Triangle className="w-4 h-4" />,
      color: 'bg-red-500',
      description: 'Mistral AI integration'
    }
  ];

  const workflowExamples = [
    {
      name: 'Credit Review Automation',
      description: 'Automated credit assessment with multi-LLM analysis',
      nodes: 7,
      executions: '12.5K',
      success: '98.5%',
      category: 'Financial Services'
    },
    {
      name: 'Customer Support Routing',
      description: 'Intelligent ticket routing and response generation',
      nodes: 9,
      executions: '45.2K',
      success: '96.8%',
      category: 'Customer Service'
    },
    {
      name: 'Document Processing Pipeline',
      description: 'Extract, analyze, and classify business documents',
      nodes: 6,
      executions: '8.7K',
      success: '99.1%',
      category: 'Document Management'
    }
  ];

  const workflowStats = [
    { metric: '1000+', label: 'Workflows Created', icon: <Workflow className="w-5 h-5" /> },
    { metric: '100k+', label: 'Executions', icon: <Play className="w-5 h-5" /> },
    { metric: '99.9%', label: 'Uptime', icon: <Activity className="w-5 h-5" /> },
    { metric: '<100ms', label: 'Avg Latency', icon: <Zap className="w-5 h-5" /> }
  ];

  const features = [
    {
      icon: <MousePointer className="w-8 h-8" />,
      title: 'Visual Drag & Drop Builder',
      description: 'Create complex workflows with an intuitive visual interface',
      highlights: ['No coding required', 'Real-time preview', 'Auto-validation', 'Template library']
    },
    {
      icon: <Brain className="w-8 h-8" />,
      title: 'Multi-LLM Orchestration',
      description: 'Seamlessly integrate and switch between multiple AI models',
      highlights: ['GPT-4, Claude, Gemini', 'Dynamic model selection', 'Cost optimization', 'Fallback handling']
    },
    {
      icon: <GitBranch className="w-8 h-8" />,
      title: 'Advanced Logic & Branching',
      description: 'Handle complex business logic with conditional flows',
      highlights: ['If-else conditions', 'Loop iterations', 'Error handling', 'Parallel execution']
    },
    {
      icon: <Network className="w-8 h-8" />,
      title: 'Enterprise Integrations',
      description: 'Connect with your existing tools and systems',
      highlights: ['API integrations', 'Database connections', 'Webhook triggers', 'SSO support']
    },
    {
      icon: <Activity className="w-8 h-8" />,
      title: 'Real-time Monitoring',
      description: 'Track workflow performance and execution metrics',
      highlights: ['Live dashboards', 'Error tracking', 'Performance analytics', 'Alert system']
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: 'Enterprise Security',
      description: 'Built-in security and compliance for sensitive workflows',
      highlights: ['Data encryption', 'Access controls', 'Audit logs', 'Compliance tools']
    }
  ];

  const runWorkflowDemo = () => {
    setIsRunning(true);
    setCurrentStep(0);
    const steps = ['Start', 'Load Data', 'Analyze', 'Validate', 'Decide', 'Complete'];
    
    const interval = setInterval(() => {
      setCurrentStep(prev => {
        if (prev >= steps.length - 1) {
          clearInterval(interval);
          setTimeout(() => setIsRunning(false), 1000);
          return prev;
        }
        return prev + 1;
      });
    }, 800);
  };

  return (
    <main className="bg-black text-white font-sans overflow-hidden">
      {/* Dynamic Background */}
      <div className="fixed inset-0 pointer-events-none">
        <div
          className="absolute inset-0 opacity-30 transition-all duration-1000"
          style={{
            background: `radial-gradient(600px circle at ${mousePosition.x}px ${mousePosition.y}px, rgba(139, 92, 246, 0.15), transparent 40%)`
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-transparent to-blue-900/20" />
        
        {/* Animated Network Pattern */}
        <div className="absolute inset-0 opacity-10">
          <svg className="w-full h-full" viewBox="0 0 1200 800">
            <defs>
              <pattern id="grid" width="50" height="50" patternUnits="userSpaceOnUse">
                <circle cx="25" cy="25" r="1" fill="currentColor" className="text-purple-400" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          {/* Floating Badge */}
          {/* <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-purple-600/30 to-blue-600/30 backdrop-blur-xl border border-purple-500/50 rounded-full px-6 py-3 mb-8 animate-bounce">
            <Workflow className="w-5 h-5 text-purple-400 animate-pulse" />
            <span className="text-sm font-medium text-purple-300">Visual Workflow Engine</span>
            <div className="w-2 h-2 bg-green-400 rounded-full animate-ping" />
          </div> */}
          <br/><br/><br/>
          {/* Main Title */}
          <h1 className="text-6xl md:text-8xl font-black mb-8 bg-gradient-to-r from-white via-purple-200 to-blue-200 bg-clip-text text-transparent">
            Selfic Workflows
            <span className="block mt-4 text-4xl md:text-6xl bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 bg-clip-text text-transparent">
              Orchestrate Intelligence
            </span>
          </h1>
          
          {/* Subtitle */}
          <div className="opacity-0 animate-fade-in-up" style={{ animationDelay: '0.5s', animationFillMode: 'forwards' }}>
            <p className="text-2xl text-gray-300 max-w-4xl mx-auto leading-relaxed mb-12">
              Build sophisticated business workflows with drag-and-drop simplicity. 
              Orchestrate multiple LLMs, integrate systems, and automate complex processes without coding.
            </p>
          </div>

          {/* Interactive CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-6 justify-center mb-16 opacity-0 animate-fade-in-up" style={{ animationDelay: '1s', animationFillMode: 'forwards' }}>
            <button 
              onClick={runWorkflowDemo}
              className="group relative bg-gradient-to-r from-purple-600 to-blue-600 text-white px-12 py-6 rounded-2xl font-bold text-lg hover:scale-105 transition-all duration-300 shadow-2xl shadow-purple-500/25 hover:shadow-purple-500/50"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-purple-400 to-blue-400 rounded-2xl blur opacity-0 group-hover:opacity-50 transition-opacity duration-300" />
              <div className="relative flex items-center space-x-3">
                <Play className="w-6 h-6 group-hover:scale-110 transition-transform duration-300" />
                <span>Run Demo Workflow</span>
                <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform duration-300" />
              </div>
            </button>
            
            <button className="group border-2 border-white/20 text-white px-12 py-6 rounded-2xl font-bold text-lg hover:bg-white/10 transition-all duration-300 backdrop-blur-xl">
              <div className="flex items-center space-x-3">
                <MousePointer className="w-6 h-6 group-hover:scale-110 transition-transform duration-300" />
                <span>Try Builder</span>
              </div>
            </button>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto opacity-0 animate-fade-in-up" style={{ animationDelay: '1.5s', animationFillMode: 'forwards' }}>
            {workflowStats.map((stat, index) => (
              <div 
                key={index} 
                className="group bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl border border-white/20 rounded-2xl p-6 hover:border-purple-500/50 transition-all duration-300 hover:scale-105"
              >
                <div className="text-purple-400 mb-3 flex justify-center group-hover:scale-110 transition-transform duration-300">
                  {stat.icon}
                </div>
                <div className="text-3xl font-bold text-white mb-2 group-hover:text-purple-300 transition-colors duration-300">
                  {stat.metric}
                </div>
                <div className="text-sm text-gray-400">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* State-of-the-Art Workflow Simulation */}
      <section className="relative py-32 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-5xl md:text-6xl font-bold mb-8 bg-gradient-to-r from-white to-purple-200 bg-clip-text text-transparent">
              Visual Workflow Builder
            </h2>
            <p className="text-2xl text-gray-300 max-w-3xl mx-auto">
              Experience intelligent workflow orchestration with real-time execution and debugging
            </p>
          </div>

          {/* Professional Workflow Interface */}
          <div className="relative bg-gradient-to-br from-gray-900/90 to-black/90 backdrop-blur-2xl border border-white/20 rounded-3xl overflow-hidden shadow-2xl">
            {/* Advanced Control Header */}
            <div className="flex items-center justify-between p-6 border-b border-white/10 bg-gradient-to-r from-gray-900/50 to-black/50">
              <div className="flex items-center space-x-6">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-gradient-to-r from-purple-500 to-blue-500 rounded-lg">
                    <Workflow className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">Workflow Studio Pro</h3>
                    <div className="text-xs text-gray-400">Credit Review Automation</div>
                  </div>
                </div>
                
                {/* Workflow Modes */}
                <div className="flex space-x-2">
                  <button className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                    !isRunning ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/25' : 'bg-white/10 text-gray-300 hover:bg-white/20'
                  }`}>
                    <div className="flex items-center space-x-2">
                      <Settings className="w-4 h-4" />
                      <span>Design</span>
                    </div>
                  </button>
                  <button className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                    isRunning ? 'bg-green-500 text-white shadow-lg shadow-green-500/25' : 'bg-white/10 text-gray-300 hover:bg-white/20'
                  }`}>
                    <div className="flex items-center space-x-2">
                      <Activity className="w-4 h-4" />
                      <span>Live</span>
                    </div>
                  </button>
                  <button className="px-4 py-2 rounded-lg text-sm font-medium bg-white/10 text-gray-300 hover:bg-white/20 transition-all duration-300">
                    <div className="flex items-center space-x-2">
                      <Eye className="w-4 h-4" />
                      <span>Debug</span>
                    </div>
                  </button>
                </div>
              </div>

              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-3 px-4 py-2 bg-white/10 rounded-lg">
                  <div className={`w-2 h-2 rounded-full ${isRunning ? 'bg-green-400 animate-pulse' : 'bg-gray-400'}`} />
                  <span className="text-xs text-gray-300">
                    {isRunning ? 'Executing' : 'Ready'}
                  </span>
                </div>
                <button 
                  onClick={runWorkflowDemo}
                  className={`px-6 py-2 rounded-xl font-medium transition-all duration-300 flex items-center space-x-2 ${
                    isRunning 
                      ? 'bg-gradient-to-r from-red-500 to-red-600 text-white shadow-lg shadow-red-500/25' 
                      : 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white shadow-lg shadow-green-500/25'
                  }`}
                >
                  {isRunning ? (
                    <>
                      <Pause className="w-4 h-4" />
                      <span>Stop</span>
                    </>
                  ) : (
                    <>
                      <Play className="w-4 h-4" />
                      <span>Execute</span>
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Workflow Canvas */}
            <div className="relative h-[800px] bg-gradient-to-br from-slate-900 to-slate-800 overflow-hidden">
              {/* Professional Toolbar */}
              <div className="absolute top-4 left-4 flex space-x-2 z-10">
                <button className="p-3 bg-gray-800/80 hover:bg-gray-700/80 rounded-lg transition-colors border border-gray-600/50">
                  <MousePointer className="w-4 h-4 text-gray-300" />
                </button>
                <button className="p-3 bg-gray-800/80 hover:bg-gray-700/80 rounded-lg transition-colors border border-gray-600/50">
                  <Plus className="w-4 h-4 text-gray-300" />
                </button>
                <button className="p-3 bg-gray-800/80 hover:bg-gray-700/80 rounded-lg transition-colors border border-gray-600/50">
                  <Route className="w-4 h-4 text-gray-300" />
                </button>
              </div>

              {/* Enhanced Mini Map */}
              <div className="absolute top-4 right-4 w-48 h-32 bg-gray-800/90 border border-gray-600/50 rounded-xl p-3 z-10">
                <div className="text-xs text-gray-400 mb-2 font-medium">Mini Map</div>
                <div className="relative w-full h-full bg-slate-700/50 rounded-lg overflow-hidden">
                  <div className="absolute inset-0">
                    <div className="grid grid-cols-8 grid-rows-5 gap-0.5 h-full">
                      {Array.from({ length: 40 }).map((_, i) => (
                        <div key={i} className="bg-purple-400/20 rounded-sm" />
                      ))}
                    </div>
                    {/* Mini workflow representation */}
                    <div className="absolute top-2 left-2 w-3 h-2 bg-green-400 rounded-sm" />
                    <div className="absolute top-6 left-8 w-4 h-2 bg-gray-400 rounded-sm" />
                    <div className="absolute top-10 left-4 w-5 h-2 bg-purple-400 rounded-sm" />
                    <div className="absolute top-10 right-6 w-3 h-2 bg-orange-400 rounded-sm" />
                    <div className="absolute bottom-4 left-1/2 w-4 h-2 bg-yellow-400 rounded-sm transform -translate-x-1/2" />
                  </div>
                </div>
              </div>

              {/* SVG Workflow with Perfect Positioning */}
              <svg className="absolute inset-0 w-full h-full" viewBox="0 0 1200 800" style={{ zIndex: 1 }}>
                <defs>
                  <linearGradient id="connectionGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="rgba(59, 130, 246, 0.8)" />
                    <stop offset="50%" stopColor="rgba(139, 92, 246, 0.8)" />
                    <stop offset="100%" stopColor="rgba(16, 185, 129, 0.8)" />
                  </linearGradient>
                  <linearGradient id="activeFlow" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="rgba(34, 197, 94, 1)" />
                    <stop offset="50%" stopColor="rgba(59, 130, 246, 1)" />
                    <stop offset="100%" stopColor="rgba(139, 92, 246, 1)" />
                  </linearGradient>
                </defs>

                {/* Connection Lines - Matching Screenshot Exactly */}
                <g>
                  {/* Start to Load Data */}
                  <path 
                    d="M 350 120 Q 400 140 520 160" 
                    stroke={isRunning && currentStep >= 1 ? "url(#activeFlow)" : "rgba(59, 130, 246, 0.6)"} 
                    strokeWidth="3" 
                    fill="none"
                    strokeDasharray="8 6"
                    className="transition-all duration-500"
                    style={{ 
                      animation: isRunning && currentStep >= 1 ? 'dashMove 2s linear infinite' : 'none'
                    }}
                  />
                  
                  {/* Load Data to Selfic Agent */}
                  <path 
                    d="M 520 200 Q 450 250 350 320" 
                    stroke={isRunning && currentStep >= 2 ? "url(#activeFlow)" : "rgba(59, 130, 246, 0.6)"} 
                    strokeWidth="3" 
                    fill="none"
                    strokeDasharray="8 6"
                    className="transition-all duration-500"
                    style={{ 
                      animation: isRunning && currentStep >= 2 ? 'dashMove 2s linear infinite' : 'none',
                      animationDelay: '0.3s'
                    }}
                  />
                  
                  {/* Load Data to Validate */}
                  <path 
                    d="M 650 200 Q 750 250 850 320" 
                    stroke={isRunning && currentStep >= 3 ? "url(#activeFlow)" : "rgba(59, 130, 246, 0.6)"} 
                    strokeWidth="3" 
                    fill="none"
                    strokeDasharray="8 6"
                    className="transition-all duration-500"
                    style={{ 
                      animation: isRunning && currentStep >= 3 ? 'dashMove 2s linear infinite' : 'none',
                      animationDelay: '0.6s'
                    }}
                  />
                  
                  {/* Selfic Agent to Decision */}
                  <path 
                    d="M 450 400 Q 500 450 580 500" 
                    stroke={isRunning && currentStep >= 4 ? "url(#activeFlow)" : "rgba(139, 92, 246, 0.6)"} 
                    strokeWidth="3" 
                    fill="none"
                    strokeDasharray="8 6"
                    className="transition-all duration-500"
                    style={{ 
                      animation: isRunning && currentStep >= 4 ? 'dashMove 2s linear infinite' : 'none',
                      animationDelay: '0.9s'
                    }}
                  />
                  
                  {/* Validate to Decision */}
                  <path 
                    d="M 850 400 Q 750 450 680 500" 
                    stroke={isRunning && currentStep >= 4 ? "url(#activeFlow)" : "rgba(139, 92, 246, 0.6)"} 
                    strokeWidth="3" 
                    fill="none"
                    strokeDasharray="8 6"
                    className="transition-all duration-500"
                    style={{ 
                      animation: isRunning && currentStep >= 4 ? 'dashMove 2s linear infinite' : 'none',
                      animationDelay: '1s'
                    }}
                  />
                  
                  {/* Decision to Auto Approve */}
                  <path 
                    d="M 580 540 Q 450 600 300 660" 
                    stroke={isRunning && currentStep >= 5 ? "url(#activeFlow)" : "rgba(16, 185, 129, 0.6)"} 
                    strokeWidth="3" 
                    fill="none"
                    strokeDasharray="8 6"
                    className="transition-all duration-500"
                    style={{ 
                      animation: isRunning && currentStep >= 5 ? 'dashMove 2s linear infinite' : 'none',
                      animationDelay: '1.2s'
                    }}
                  />
                  
                  {/* Decision to Manual Review */}
                  <path 
                    d="M 680 540 Q 800 600 900 660" 
                    stroke={isRunning && currentStep >= 5 ? "url(#activeFlow)" : "rgba(16, 185, 129, 0.6)"} 
                    strokeWidth="3" 
                    fill="none"
                    strokeDasharray="8 6"
                    className="transition-all duration-500"
                    style={{ 
                      animation: isRunning && currentStep >= 5 ? 'dashMove 2s linear infinite' : 'none',
                      animationDelay: '1.3s'
                    }}
                  />
                  
                  {/* Converge to Final */}
                  <path 
                    d="M 350 700 Q 500 720 600 720" 
                    stroke={isRunning && currentStep >= 5 ? "url(#activeFlow)" : "rgba(16, 185, 129, 0.6)"} 
                    strokeWidth="3" 
                    fill="none"
                    strokeDasharray="8 6"
                    className="transition-all duration-500"
                    style={{ 
                      animation: isRunning && currentStep >= 5 ? 'dashMove 2s linear infinite' : 'none',
                      animationDelay: '1.5s'
                    }}
                  />
                  
                  <path 
                    d="M 850 700 Q 750 720 600 720" 
                    stroke={isRunning && currentStep >= 5 ? "url(#activeFlow)" : "rgba(16, 185, 129, 0.6)"} 
                    strokeWidth="3" 
                    fill="none"
                    strokeDasharray="8 6"
                    className="transition-all duration-500"
                    style={{ 
                      animation: isRunning && currentStep >= 5 ? 'dashMove 2s linear infinite' : 'none',
                      animationDelay: '1.6s'
                    }}
                  />
                </g>
              </svg>

              {/* Workflow Nodes - Positioned to Match Screenshot */}
              <div className="absolute inset-0" style={{ zIndex: 2 }}>
                {/* Start Node */}
                <div 
                  className={`absolute top-16 left-24 transform transition-all duration-700 ${
                    isRunning && currentStep >= 0 ? 'scale-110' : 'scale-100'
                  }`}
                >
                  <div className={`group relative px-6 py-4 bg-gradient-to-r from-green-500 to-green-600 rounded-2xl text-white font-semibold shadow-2xl border-2 transition-all duration-500 ${
                    isRunning && currentStep >= 0 
                      ? 'border-green-300 shadow-green-500/50' 
                      : 'border-green-400/50'
                  }`}>
                    <div className="flex items-center space-x-3">
                      <Circle className="w-5 h-5" />
                      <span className="text-lg">Start Credit Review</span>
                    </div>
                    {isRunning && currentStep >= 0 && (
                      <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-300 rounded-full animate-ping" />
                    )}
                  </div>
                </div>

                {/* Load Data Node */}
                <div 
                  className={`absolute top-32 left-96 transform transition-all duration-700 ${
                    isRunning && currentStep >= 1 ? 'scale-110' : 'scale-100'
                  }`}
                >
                  <div className={`group relative px-6 py-4 bg-gradient-to-r from-gray-600 to-gray-700 rounded-2xl text-white shadow-2xl border-2 transition-all duration-500 ${
                    isRunning && currentStep >= 1 
                      ? 'border-blue-300 shadow-blue-500/50' 
                      : 'border-gray-400/50'
                  }`}>
                    <div className="flex items-center space-x-3 mb-2">
                      <Database className="w-5 h-5" />
                      <span className="text-lg font-semibold">Load Data</span>
                    </div>
                    <div className="text-sm text-gray-300">(Snowflake / SQL / Oracle)</div>
                    {isRunning && currentStep >= 1 && (
                      <div className="absolute -top-1 -right-1 w-3 h-3 bg-blue-300 rounded-full animate-ping" />
                    )}
                  </div>
                </div>

                {/* Selfic Agent Node */}
                <div 
                  className={`absolute top-64 left-8 transform transition-all duration-700 ${
                    isRunning && currentStep >= 2 ? 'scale-110' : 'scale-100'
                  }`}
                >
                  <div className={`group relative px-6 py-5 bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl text-white shadow-2xl border-2 max-w-md transition-all duration-500 ${
                    isRunning && currentStep >= 2 
                      ? 'border-purple-300 shadow-purple-500/50' 
                      : 'border-purple-400/50'
                  }`}>
                    <div className="flex items-center space-x-3 mb-3">
                      <Bot className="w-5 h-5" />
                      <span className="text-lg font-semibold">Selfic Agent:</span>
                    </div>
                    <div className="text-sm text-purple-100 leading-relaxed">
                      Analyze request, retrieve schema/doc context,<br/>
                      generate logic (LLM or custom algo), apply policies
                    </div>
                    {isRunning && currentStep >= 2 && (
                      <div className="absolute -top-1 -right-1 w-3 h-3 bg-purple-300 rounded-full animate-ping" />
                    )}
                  </div>
                </div>

                {/* Validation Node */}
                <div 
                  className={`absolute top-64 right-24 transform transition-all duration-700 ${
                    isRunning && currentStep >= 3 ? 'scale-110' : 'scale-100'
                  }`}
                >
                  <div className={`group relative px-6 py-5 bg-gradient-to-r from-orange-500 to-red-500 rounded-2xl text-white shadow-2xl border-2 transition-all duration-500 ${
                    isRunning && currentStep >= 3 
                      ? 'border-orange-300 shadow-orange-500/50' 
                      : 'border-orange-400/50'
                  }`}>
                    <div className="flex items-center space-x-3 mb-3">
                      <Shield className="w-5 h-5" />
                      <span className="text-lg font-semibold">Validate</span>
                    </div>
                    <div className="text-sm text-orange-100">
                      Business Rules<br/>& Limits
                    </div>
                    {isRunning && currentStep >= 3 && (
                      <div className="absolute -top-1 -right-1 w-3 h-3 bg-orange-300 rounded-full animate-ping" />
                    )}
                  </div>
                </div>

                {/* Decision Node */}
                <div 
                  className={`absolute top-96 left-1/2 transform -translate-x-1/2 transition-all duration-700 ${
                    isRunning && currentStep >= 4 ? 'scale-110' : 'scale-100'
                  }`}
                >
                  <div className={`group relative px-6 py-4 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-2xl text-white shadow-2xl border-2 transition-all duration-500 ${
                    isRunning && currentStep >= 4 
                      ? 'border-yellow-300 shadow-yellow-500/50' 
                      : 'border-yellow-400/50'
                  }`}>
                    <div className="flex items-center space-x-3">
                      <GitBranch className="w-5 h-5" />
                      <span className="text-lg font-semibold">Eligible for Credit?</span>
                    </div>
                    {isRunning && currentStep >= 4 && (
                      <div className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-300 rounded-full animate-ping" />
                    )}
                  </div>
                </div>

                {/* Auto Approve Node */}
                <div 
                  className={`absolute bottom-32 left-16 transform transition-all duration-700 ${
                    isRunning && currentStep >= 5 ? 'scale-110' : 'scale-100'
                  }`}
                >
                  <div className={`group relative px-6 py-4 bg-gradient-to-r from-green-500 to-green-600 rounded-2xl text-white shadow-2xl border-2 transition-all duration-500 ${
                    isRunning && currentStep >= 5 
                      ? 'border-green-300 shadow-green-500/50' 
                      : 'border-green-400/50'
                  }`}>
                    <div className="flex items-center space-x-3">
                      <CheckCircle className="w-5 h-5" />
                      <span className="text-lg font-semibold">Auto Approve</span>
                    </div>
                    {isRunning && currentStep >= 5 && (
                      <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-300 rounded-full animate-ping" />
                    )}
                  </div>
                </div>

                {/* Manual Review Node */}
                <div 
                  className={`absolute bottom-32 right-20 transform transition-all duration-700 ${
                    isRunning && currentStep >= 5 ? 'scale-110' : 'scale-100'
                  }`}
                >
                  <div className={`group relative px-6 py-5 bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl text-white shadow-2xl border-2 transition-all duration-500 ${
                    isRunning && currentStep >= 5 
                      ? 'border-blue-300 shadow-blue-500/50' 
                      : 'border-blue-400/50'
                  }`}>
                    <div className="flex items-center space-x-3 mb-2">
                      <UserCheck className="w-5 h-5" />
                      <span className="text-lg font-semibold">Escalate for</span>
                    </div>
                    <div className="text-sm text-blue-100">Manual Review</div>
                    {isRunning && currentStep >= 5 && (
                      <div className="absolute -top-1 -right-1 w-3 h-3 bg-blue-300 rounded-full animate-ping" />
                    )}
                  </div>
                </div>

                {/* Final Node */}
                <div 
                  className={`absolute bottom-8 left-1/2 transform -translate-x-1/2 transition-all duration-700 ${
                    isRunning && currentStep >= 5 ? 'scale-110' : 'scale-100'
                  }`}
                >
                  <div className={`group relative px-6 py-4 bg-gradient-to-r from-gray-500 to-gray-600 rounded-2xl text-white shadow-2xl border-2 transition-all duration-500 ${
                    isRunning && currentStep >= 5 
                      ? 'border-gray-300 shadow-gray-500/50' 
                      : 'border-gray-400/50'
                  }`}>
                    <div className="flex items-center space-x-3">
                      <MessageSquare className="w-5 h-5" />
                      <span className="text-lg font-semibold">Notify and Log Decision</span>
                    </div>
                    {isRunning && currentStep >= 5 && (
                      <div className="absolute -top-1 -right-1 w-3 h-3 bg-gray-300 rounded-full animate-ping" />
                    )}
                  </div>
                </div>
              </div>

              {/* Professional Execution Panel */}
              {isRunning && (
                <div className="absolute bottom-6 right-6 bg-gradient-to-br from-gray-900/95 to-black/95 backdrop-blur-xl border border-green-500/50 rounded-2xl p-6 shadow-2xl min-w-[300px]">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="relative">
                        <div className="w-4 h-4 bg-green-400 rounded-full animate-ping" />
                        <div className="absolute inset-0 w-4 h-4 bg-green-400 rounded-full animate-pulse" />
                      </div>
                      <div>
                        <div className="text-green-300 font-semibold">Workflow Executing</div>
                        <div className="text-gray-400 text-xs">Credit Review Process</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-white font-mono text-sm">Step {currentStep + 1}/6</div>
                      <div className="text-gray-400 text-xs">{Math.round(((currentStep + 1) / 6) * 100)}% Complete</div>
                    </div>
                  </div>
                  
                  <div className="w-full bg-gray-800 rounded-full h-2 mb-4">
                    <div 
                      className="bg-gradient-to-r from-green-400 via-blue-400 to-purple-400 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${((currentStep + 1) / 6) * 100}%` }}
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 text-xs">
                    <div className="bg-gray-800/50 rounded-lg p-3">
                      <div className="text-gray-400">Duration</div>
                      <div className="text-white font-mono">2.4s</div>
                    </div>
                    <div className="bg-gray-800/50 rounded-lg p-3">
                      <div className="text-gray-400">Cost</div>
                      <div className="text-white font-mono">$0.03</div>
                    </div>
                    <div className="bg-gray-800/50 rounded-lg p-3">
                      <div className="text-gray-400">Throughput</div>
                      <div className="text-white font-mono">450/min</div>
                    </div>
                    <div className="bg-gray-800/50 rounded-lg p-3">
                      <div className="text-gray-400">Success Rate</div>
                      <div className="text-green-400 font-mono">99.1%</div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Professional Status Footer */}
            <div className="border-t border-white/10 bg-gradient-to-r from-gray-900/80 to-black/80 p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-8 text-sm">
                  <div className="flex items-center space-x-2 text-gray-400">
                    <Activity className="w-4 h-4 text-green-400" />
                    <span>System: <span className="text-green-400 font-medium">Operational</span></span>
                  </div>
                  <div className="flex items-center space-x-2 text-gray-400">
                    <Clock className="w-4 h-4 text-blue-400" />
                    <span>Avg Latency: <span className="text-white font-medium">120ms</span></span>
                  </div>
                  <div className="flex items-center space-x-2 text-gray-400">
                    <TrendingUp className="w-4 h-4 text-purple-400" />
                    <span>Nodes: <span className="text-white font-medium">7 Active</span></span>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <button className="px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:scale-105 transition-transform duration-300 text-sm font-medium">
                    Export Workflow
                  </button>
                  <button className="px-4 py-2 border border-white/20 text-white rounded-lg hover:bg-white/10 transition-colors text-sm font-medium">
                    View Logs
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Showcase */}
      <section className="relative py-32 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-5xl md:text-6xl font-bold mb-8 bg-gradient-to-r from-white to-purple-200 bg-clip-text text-transparent">
              Enterprise Workflow Engine
            </h2>
            <p className="text-2xl text-gray-300 max-w-3xl mx-auto">
              Everything you need to automate complex business processes at scale
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="group relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl border border-white/20 rounded-3xl p-8 hover:border-purple-500/50 transition-all duration-500 cursor-pointer hover:scale-105"
              >
                <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-purple-500/10 to-blue-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                
                <div className="relative">
                  <div className="text-purple-400 mb-6 group-hover:scale-110 transition-transform duration-300">
                    {feature.icon}
                  </div>
                  
                  <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-purple-300 transition-colors duration-300">
                    {feature.title}
                  </h3>
                  
                  <p className="text-gray-300 mb-6 leading-relaxed">
                    {feature.description}
                  </p>
                  
                  <div className="space-y-2">
                    {feature.highlights.map((highlight, i) => (
                      <div key={i} className="flex items-center space-x-2">
                        <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                        <span className="text-sm text-gray-400">{highlight}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Workflow Examples */}
      <section className="relative py-32 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-bold mb-8 bg-gradient-to-r from-white to-purple-200 bg-clip-text text-transparent">
              Real-World Workflows
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              See how businesses are automating critical processes with Selfic Workflows
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {workflowExamples.map((workflow, index) => (
              <div 
                key={index}
                className={`bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl border border-white/20 rounded-2xl p-8 transition-all duration-500 cursor-pointer ${
                  activeWorkflow === index ? 'scale-105 border-purple-500/50 shadow-xl shadow-purple-500/20' : 'hover:border-white/30'
                }`}
                onClick={() => setActiveWorkflow(index)}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                    index === 0 ? 'bg-blue-500/20 text-blue-400' :
                    index === 1 ? 'bg-green-500/20 text-green-400' :
                    'bg-purple-500/20 text-purple-400'
                  }`}>
                    {workflow.category}
                  </div>
                  <div className="flex items-center space-x-1">
                    <Activity className="w-4 h-4 text-green-400" />
                    <span className="text-xs text-green-400">Live</span>
                  </div>
                </div>

                <h3 className="text-xl font-bold text-white mb-3">{workflow.name}</h3>
                <p className="text-gray-300 text-sm mb-6 leading-relaxed">{workflow.description}</p>

                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-lg font-bold text-white">{workflow.nodes}</div>
                    <div className="text-xs text-gray-400">Nodes</div>
                  </div>
                  <div>
                    <div className="text-lg font-bold text-white">{workflow.executions}</div>
                    <div className="text-xs text-gray-400">Runs</div>
                  </div>
                  <div>
                    <div className="text-lg font-bold text-green-400">{workflow.success}</div>
                    <div className="text-xs text-gray-400">Success</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="relative py-32 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto text-center">
          <div className="relative bg-gradient-to-br from-purple-600/30 to-blue-600/30 backdrop-blur-2xl border border-purple-500/50 rounded-3xl p-16 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-purple-500/10 to-blue-500/10 opacity-50" />
            
            <div className="relative">
              <div className="inline-block p-4 bg-gradient-to-r from-purple-500 to-blue-500 rounded-2xl mb-8 animate-bounce">
                <Workflow className="w-12 h-12 text-white" />
              </div>
              
              <h2 className="text-4xl md:text-6xl font-bold mb-8 bg-gradient-to-r from-white to-purple-200 bg-clip-text text-transparent">
                Ready to Automate?
              </h2>
              
              <p className="text-xl text-gray-300 mb-12 leading-relaxed max-w-3xl mx-auto">
                Start building intelligent workflows that connect your systems, orchestrate AI models, 
                and automate complex business processes. No coding required.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-6 justify-center">
                <button className="group relative bg-gradient-to-r from-purple-600 to-blue-600 text-white px-12 py-6 rounded-2xl font-bold text-lg hover:scale-105 transition-all duration-300 shadow-2xl shadow-purple-500/25">
                  <div className="absolute inset-0 bg-gradient-to-r from-purple-400 to-blue-400 rounded-2xl blur opacity-0 group-hover:opacity-50 transition-opacity duration-300" />
                  <div className="relative flex items-center space-x-3">
                    <MousePointer className="w-6 h-6 group-hover:scale-110 transition-transform duration-300" />
                    <span>Start Building Workflows</span>
                    <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform duration-300" />
                  </div>
                </button>
                
                <button className="group border-2 border-white/30 text-white px-12 py-6 rounded-2xl font-bold text-lg hover:bg-white/10 transition-all duration-300 backdrop-blur-xl">
                  <div className="flex items-center space-x-3">
                    <Eye className="w-6 h-6 group-hover:scale-110 transition-transform duration-300" />
                    <span>View Templates</span>
                  </div>
                </button>
              </div>
              
              <div className="mt-12 text-sm text-gray-400">
                Free trial • No credit card required • Deploy in minutes
              </div>
            </div>
          </div>
        </div>
      </section>

      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes fade-in-up {
            from {
              opacity: 0;
              transform: translateY(30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          @keyframes dashMove {
            from {
              stroke-dashoffset: 20;
            }
            to {
              stroke-dashoffset: 0;
            }
          }

          .animate-fade-in-up {
            animation: fade-in-up 0.8s ease-out;
          }

          .custom-scrollbar::-webkit-scrollbar {
            width: 4px;
          }

          .custom-scrollbar::-webkit-scrollbar-track {
            background: rgba(255, 255, 255, 0.1);
            border-radius: 2px;
          }

          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: rgba(139, 92, 246, 0.5);
            border-radius: 2px;
          }

          .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background: rgba(139, 92, 246, 0.8);
          }
        `
      }} />
    </main>
  );
};

export default SelficWorkflowsBody;